import { AppParam, AppParamsEnum } from "@/shared/models/appParam";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import { isNumber } from "lodash/fp";
import Vue from "vue";
import { Currency } from "@/shared/models/currency";
import { TenantCustomerModel } from "@/shared/models/tenantCustomer";
import { EVisitorIntegrationEnum, EVisitorIntegrationNameEnum } from "@/shared/models/enums.type";
import { EVisitorIntegrationModel } from "@/shared/models/eVisitor";
import TenantWhatsAppIntegrationStatusViewModel from "@/shared/models/tenantWhatsAppIntegrationViewModel";

export interface UpdateAppParamModel {
  appParamId: number;
  value: string;
}

const initialState = {
  appParams: [] as AppParam[],
  tenantPaysVat: false,
  tenantUsesInputPrice: false,
  tenantAutoEmailsFrom: "",
  tenantWhatsAppFromNumber: "",
  tenantIsFiscalizationObligor: true,
  tenantPaymentGatewayIntegrationId: 0,
  tenantEVisitorIntegrationId: 0,
  tenantWhatsAppIntegrationEnabled: false,
  tenantEInvoiceIntegrationId: 0,
  tenantWhatsAppIntegrationStatus: 1, // not started is the default
  typeId: null,
  tenantCustomer: null as TenantCustomerModel | null,
  countryId: null,
  appParamsFormSearchValue: ""
};

const state = { ...initialState };

const getters: GetterTree<typeof state, any> = {
  appParamsFormSearchValue: (state) => state.appParamsFormSearchValue,
  appParams: (state) => state.appParams,
  workingCurrency: (state, getters, rootState, rootGetters) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.TenantWorkingCurrency
    )?.value;
    if (isNumber(val)) {
      const currencies = rootGetters["codebooks/currencies"] as Currency[];
      return currencies.find((x) => x.id === val);
    }
  },
  formalCurrency: (state, getters, rootState, rootGetters) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.TenantFormalCurrency
    )?.value;
    if (isNumber(val)) {
      const currencies = rootGetters["codebooks/currencies"] as Currency[];
      return currencies.find((x) => x.id === val);
    }
  },
  multiCurrencyInvoices: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.EnableMultiCurrencyInvoices
    )?.value;
    return val;
  },
  tenantPaysVat: (state) => state.tenantPaysVat,
  tenantIsFiscalizationObligor: (state) => state.tenantIsFiscalizationObligor,
  tenantUsesInputPrice: (state) => state.tenantUsesInputPrice,
  tenantAutoEmailsFrom: (state) => state.tenantAutoEmailsFrom,
  tenantWhatsAppFromNumber: (state) => state.tenantWhatsAppFromNumber,
  reservationFormBlankPriceOption: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.ReservationFormBlankPriceOption
    )?.value;
    return val;
  },
  reservationShowRatePlanPopup: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.ReservationShowRatePlanPopup
    )?.value;
    return val;
  },
  showOwnerPriceOffset: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.ShowOwnerPriceOffset
    )?.value;
    return val;
  },
  reservationShowHolderInfo: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.ReservationShowHolderInfo
    )?.value;
    return val;
  },
  reservationShowGuestInfo: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.ReservationShowGuestInfo
    )?.value;
    return val;
  },
  checkOutOnEVisitorState: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.CheckOutOnEVisitorState
    )?.value;
    return val;
  },
  checkInOnEVisitorState: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.CheckInEVisitorState
    )?.value;
    return val;
  },
  statusToCheckedOutState: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.StatusToCheckedOutState
    )?.value;
    return val;
  },
  statusToCheckedInState: (state) => {
    const val = state.appParams.find(
      (x) => x.id === AppParamsEnum.StatusToCheckedInState
    )?.value;
    return val;
  },
  lineItemDistributionStrategy: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.TenantLineItemDistributionStrategy
    )?.value;

    return val;
  },
  ignoreAccomodationTextResource: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.TenantIgnoreDocTypePrefixOnAccommodation
    )?.value;

    return val;
  },
  unitRenderTypeLineOne: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.TenantUnitLineRenderLineOne
    )?.value;

    return val;
  },
  unitRenderTypeLineTwo: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.TenantUnitLineRenderLineTwo
    )?.value;

    return val;
  },
  reservationHideArrivalDeparture: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.ReservationHideArrivalDeparture
    )?.value;

    return val;
  },
  reservationHideDeposit: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.ReservationHideDeposit
    )?.value;

    return val;
  },
  reservationHideDiscount: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.ReservationHideDiscount
    )?.value;

    return val;
  },
  reservationTenantUsesServiceTypes: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.ReservationTenantUsesServiceTypes
    )?.value;

    return val;
  },
  reservationEVisitorCollapseExpandHideFacilityInfo: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.ReservationEVisitorCollapseExpandHideFacilityInfo
    )?.value;

    return val;
  },
  tenantUsesPropertiesAsBusinessUnits: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.TenantUsesPropertiesAsBusinessUnits
    )?.value;

    return val;
  },
  routePrepaymentsForOwnNameAndOtherInvoice: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.RoutePrepaymentsForOwnNameAndOtherInvoice
    )?.value;

    return val;
  },
  routeInvoicesForOwnNameAndOtherInvoice: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.RouteInvoicesForOwnNameAndOtherInvoice
    )?.value;

    return val;
  },
  calculateWithEVisitorDates: (state) => {
    const val = state.appParams.find(
      x => x.id === AppParamsEnum.ReservationEVisitorCalculateCTWithEVisitorDates
    )?.value;

    return val;
  },
  typeId: (state) => state.typeId,
  countryId: (state) => state.countryId,
  showRegistrationFormButton: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ShowRegistrationFormButton)?.value;
  },
  showCopyGuestsButton: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ShowCopyGuestsButton)?.value;
  },
  showAOSPeriodColumn: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ShowAOSPeriodColumn)?.value;
  },
  tenantCustomer: (state) => {
    return state.tenantCustomer;
  },
  tenantGeneratesOwnersInvoices: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantGeneratesOwnersInvoices)?.value;
  },
  tenantGeneratesDeliveryNotes: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantGeneratesDeliveryNotes)?.value;
  },
  tenantUsesOfficialRates: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesOfficialRates)?.value;
  },
  allowOfficialRatesInWorkingCurrency: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.AllowOfficialRatesInWorkingCurrency)?.value;
  },
  officialRatesUseSeasonNames: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.OfficialRatesUseSeasonNames)?.value;
  },
  officialRatesDateFromDefaultsToPlus1Day: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.OfficialRatesDateFromDefaultsToPlus1Day)?.value;
  },
  tenantUsesAvailabilityBlockerCalculations: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesAvailabilityBlockerCalculations)?.value;
  },
  tenantUsesLazyLoadedCalendar: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesLazyLoadedCalendar)?.value;
  },
  disableCustomAddOnServiceLineItems: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.DisableCustomAddOnServiceLineItems)?.value;
  },
  tenantShowAddOnServiceDiscount: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantShowAddOnServiceDiscount)?.value;
  },
  tenantExportsSEPA: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantExportsSEPA)?.value;
  },
  tenantExportsOwnersInvoices: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantExportsOwnersInvoices)?.value;
  },
  tenantExportsAgencyCommissionInvoices: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantExportsAgencyCommissionInvoices)?.value;
  },
  tenantExportsGuestInvoices: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantExportsGuestInvoices)?.value;
  },
  tenantHasAgencyCommissionForPrepayments: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantHasAgencyCommissionForPrepayments)?.value;
  },
  tenantOfficialRateDefaultCurrencyId: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantOfficialRateDefaultCurrency)?.value;
  },
  tenantUsesInstalments: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesInstalments)?.value;
  },
  showUnpaidPrepaymentsModal: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ShowUnpaidPrepaymentsModal)?.value;
  },
  tenantUsesAvailabilityChangelog: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesAvailabilityChangelog)?.value;
  },
  childrenAffectPAXRatePlanSelection: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ChildrenAffectPAXRatePlanSelection)?.value;
  },
  tenantPaymentGatewayIntegrationId: (state) => {
    return state.tenantPaymentGatewayIntegrationId;
  },
  tenantEVisitorIntegration: (state) => {
    let integrationName = "";
    switch (state.tenantEVisitorIntegrationId) {
      case EVisitorIntegrationEnum.HR: {
        integrationName = EVisitorIntegrationNameEnum.HR;
        break;
      }
      case EVisitorIntegrationEnum.SL: {
        integrationName = EVisitorIntegrationNameEnum.SL;
        break;
      }
      default: {
        integrationName = EVisitorIntegrationNameEnum.None;
      }
    }

    return {
      id: state.tenantEVisitorIntegrationId,
      name: integrationName
    } as EVisitorIntegrationModel;
  },
  tenantWhatsAppIntegrationStatus: (state) => {
    return state.tenantWhatsAppIntegrationStatus;
  },
  enableOwnersPartAOSOwnerInvoices: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.EnableOwnersPartAOSOwnerInvoices)?.value;
  },
  showOwnersReservationOnDashboard: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ShowOwnersReservationOnDashboard)?.value;
  },
  tenantIsAllowedToAsyncBulkGenerateInvoices: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.AsyncBulkInvoiceGeneration)?.value;
  },
  calendarOBLOSCategoriesApplicationAllowed: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.CalendarOBLOSCategoriesApplicationAllowed)?.value;
  },
  calendarRelatedRatesPriceUpdateTypeDefault: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.CalendarRelatedRatesPriceUpdateTypeDefault)?.value;
  },
  calendarRelatedRatesRestrictionsUpdateTypeDefault: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.CalendarRelatedRatesRestrictionsUpdateTypeDefault)?.value;
  },
  businessUnitShowCadastralInfo: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.BusinessUnitShowCadastralInfo)?.value;
  },
  businessUnitShowDates: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.BusinessUnitShowDates)?.value;
  },
  cashRegisterFeatureFlag: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.CashRegisterEnable)?.value;
  },
  automaticRelatedAdvanceInvoiceCancellingDefault: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.AutomaticRelatedAdvanceInvoiceCancellingDefault)?.value;
  },
  tenantCanEditPAXCategories: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantCanEditPAXCategories)?.value;
  },
  tenantAutomaticPAXCategoriesAreIndividual: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantAutomaticPAXCategoriesAreIndividual)?.value;
  },
  tenantCanEditLOSCategories: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantCanEditLOSCategories)?.value;
  },
  tenantOBRsUseRelativePricing: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantOBRsUseRelativePricing)?.value;
  },
  showPAXSuffixInRatePlanName: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantCanEditPAXCategories)?.value ||
      state.appParams.find(x => x.id === AppParamsEnum.TenantAutomaticPAXCategoriesAreIndividual)?.value
  },
  showLOSSuffixInRatePlanName: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantCanEditLOSCategories)?.value
  },
  reservationsGridFiltersByDate: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.ReservationsGridFiltersByDate)?.value
  },
  isCardTypeRequiredForCardPaymentMeans: (state) => {
    return state.appParams.find(x => x.id == AppParamsEnum.CardTypeRequiredForCardPaymentMeans)?.value;
  },
  allow0InputPriceForOfficialRate: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.Allow0InputPriceForOfficialRate)?.value
  },
  asyncRatePlanAvailabiltyUpdateThresholdSize: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.AsyncRatePlanAvailabiltyUpdateThresholdSize)?.value
  },
  tenantUsesAgencyFees: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesAgencyFees)?.value
  },
  automaticallyChangeReservationStatusToConfirmed: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.AutomaticallyChangeReservationStatusToConfirmed)?.value
  },
  tenantWhatsAppIntegrationEnabled: (state) => state.tenantWhatsAppIntegrationEnabled,
  tenantEInvoiceIntegrationId: (state) => state.tenantEInvoiceIntegrationId,
  addOnServiceCanBeRelative: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.AddOnServiceCanBeRelative)?.value
  },
  messageSchedulingSleepTimePeriod: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.MessageSchedulingSleepTimePeriod)?.value;
  },
  tenantUsesSelfCheckIn: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesSelfCheckIn)?.value;
  },
  tenantUsesBookingEngineBookingConfirmation: (state) => {
    return state.appParams.find(x => x.id === AppParamsEnum.TenantUsesBookingEngineBookingConfirmation)?.value;
  }
};

const actions: ActionTree<typeof state, any> = {
  refreshAppParams({ commit }) {
    Vue.axios.get<AppParam[]>("AppParams").then((res) => {
      commit("setAppParams", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantPaysVatParam").then((res) => {
      commit("setTenantPaysVat", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantUsesInputPrice").then((res) => {
      commit("setTenantUsesInputPrice", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantTypeId").then((res) => {
      commit("setTenantTypeId", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantAutoEmailsFrom").then((res) => {
      commit("setTenantAutoEmailsFrom", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantWhatsAppFromNumber").then((res) => {
      commit("setTenantWhatsAppFromNumber", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantIsFiscalizationObligor").then((res) => {
      commit("setTenantIsFiscalizationObligor", res.data);
    });
    Vue.axios.get<TenantCustomerModel>("AppParams/GetTenantCustomer").then((res) => {
      commit("setTenantCustomer", res.data);
    }).catch(() => {
      commit("setTenantCustomer", null);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantCountryId").then((res) => {
      commit("setTenantCountryId", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantPaymentGatewayIntegration").then((res) => {
      commit("setTenantPaymentGatewayIntegrationId", res.data);
    });
    Vue.axios.get<boolean>("AppParams/GetTenantEVisitorIntegration").then((res) => {
      commit("setTenantEVisitorIntegration", res.data);
    });
    Vue.axios.get<TenantWhatsAppIntegrationStatusViewModel>("AppParams/GetTenantWhatsAppIntegration").then((res) => {
      commit("setTenantWhatsAppIntegrationStatus", res.data.integrationStatus);
    });
    Vue.axios.get<number>("AppParams/GetEInvoiceIntegration").then((res) => {
      commit("setTenantEInvoiceIntegrationId", res.data);
    });
  },
  updateAppParam: async ({ dispatch }, model: UpdateAppParamModel) => {
    await Vue.axios.put("appParams/UpdateParam", model);
    dispatch("refreshAppParams");
    dispatch("financial/refreshModel", undefined, { root: true });
  },
  setAppParamsFormSearchValue: ({ commit }, value: string) => {
    commit("setAppParamsFormSearchValue", value);
  },
};

const mutations: MutationTree<typeof state> = {
  reset: (state) => {
    Object.assign(state, initialState);
  },
  setAppParams: (state, value) => {
    state.appParams = value.slice();
  },
  setTenantPaysVat: (state, value) => {
    state.tenantPaysVat = value;
  },
  setTenantUsesInputPrice: (state, value) => {
    state.tenantUsesInputPrice = value;
  },
  setTenantTypeId: (state, value) => {
    state.typeId = value;
  },
  setTenantAutoEmailsFrom: (state, value) => {
    state.tenantAutoEmailsFrom = value;
  },
  setTenantWhatsAppFromNumber: (state, value) => {
    state.tenantWhatsAppFromNumber = value;
  },
  setTenantIsFiscalizationObligor: (state, value) => {
    state.tenantIsFiscalizationObligor = value;
  },
  setTenantCustomer: (state, value) => {
    state.tenantCustomer = value;
  },
  setTenantCountryId: (state, value) => {
    state.countryId = value;
  },
  setTenantPaymentGatewayIntegrationId: (state, value) => {
    state.tenantPaymentGatewayIntegrationId = value;
  },
  setTenantEVisitorIntegration: (state, value) => {
    state.tenantEVisitorIntegrationId = value;
  },
  setTenantWhatsAppIntegrationStatus: (state, value) => {
    state.tenantWhatsAppIntegrationStatus = value;
  },
  setAppParamsFormSearchValue: (state, value) => {
    state.appParamsFormSearchValue = value;
  },
  setTenantEInvoiceIntegrationId: (state, value) => {
    state.tenantEInvoiceIntegrationId = value;
  },
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations,
};
